import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '@/hooks/useProducts';

const categories = [
  { id: 'all', name: 'All Products', icon: 'ri-store-2-line', color: 'from-orange-500 to-pink-600', glow: 'rgba(249,115,22,0.5)' },
  { id: 'beauty', name: 'Beauty', icon: 'ri-heart-line', color: 'from-pink-500 to-rose-600', glow: 'rgba(236,72,153,0.5)' },
  { id: 'health', name: 'Health Tech', icon: 'ri-pulse-line', color: 'from-amber-500 to-orange-600', glow: 'rgba(245,158,11,0.5)' },
  { id: 'kitchen', name: 'Kitchen', icon: 'ri-restaurant-line', color: 'from-red-500 to-rose-600', glow: 'rgba(239,68,68,0.5)' },
];

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

export default function FeaturedProducts() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);
  const [addedToCart, setAddedToCart] = useState<number | null>(null);
  const navigate = useNavigate();
  const headerReveal = useScrollReveal();
  const gridReveal = useScrollReveal();

  const { products: allProducts } = useProducts();
  const featuredProducts = allProducts.slice(0, 12);

  const filteredProducts = activeCategory === 'all'
    ? featuredProducts
    : featuredProducts.filter((p) => p.category === activeCategory);

  const handleAddToCart = (e: React.MouseEvent, product: typeof filteredProducts[0]) => {
    e.stopPropagation();
    const raw = localStorage.getItem('cart');
    const cart = raw ? JSON.parse(raw) : [];
    const existing = cart.find((i: any) => i.id === product.id);
    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    setAddedToCart(product.id);
    setTimeout(() => setAddedToCart(null), 2000);
  };

  const activeCat = categories.find((c) => c.id === activeCategory) || categories[0];

  return (
    <section id="products" className="py-24 px-6 relative overflow-hidden" data-product-shop>
      {/* Vibrant background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50" />
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #f97316, transparent)', filter: 'blur(60px)', transform: 'translate(-30%, -30%)' }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #ec4899, transparent)', filter: 'blur(60px)', transform: 'translate(30%, 30%)' }} />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div
          ref={headerReveal.ref}
          className={`text-center mb-14 transition-all duration-800 ${headerReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-semibold mb-5">
            <i className="ri-fire-line animate-heartbeat"></i>
            <span className="tracking-widest">FEATURED COLLECTION</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 gradient-text-fire leading-tight">
            Featured Products
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
            Discover our carefully curated selection of premium products
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 sm:mb-14">
          {categories.map((cat, i) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`group relative px-7 py-3.5 rounded-full font-semibold transition-all duration-400 hover:scale-110 whitespace-nowrap cursor-pointer flex items-center gap-2.5 overflow-hidden ${
                activeCategory === cat.id
                  ? `bg-gradient-to-r ${cat.color} text-white`
                  : 'bg-white/80 backdrop-blur-sm text-gray-600 hover:text-gray-900 border border-gray-200/60'
              }`}
              style={activeCategory === cat.id ? {
                boxShadow: `0 0 20px ${cat.glow}, 0 0 40px ${cat.glow.replace('0.5', '0.3')}`,
                animationDelay: `${i * 0.05}s`,
              } : {}}
            >
              <i className={`${cat.icon} text-lg transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12`}></i>
              <span>{cat.name}</span>
              {activeCategory === cat.id && (
                <span className="w-2 h-2 rounded-full bg-white/80 animate-pulse" />
              )}
              {activeCategory !== cat.id && (
                <div className={`absolute inset-0 bg-gradient-to-r ${cat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-full`} />
              )}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div
          ref={gridReveal.ref}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8"
        >
          {filteredProducts.map((product, index) => {
            const isSelected = selectedProduct === product.id;
            const isHovered = hoveredProduct === product.id;
            const isAdded = addedToCart === product.id;

            return (
              <div
                key={product.id}
                onClick={() => navigate(`/product/${product.id}`)}
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
                className={`group relative bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 ${
                  gridReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
                }`}
                style={{
                  transitionDelay: `${index * 80}ms`,
                  transform: isSelected
                    ? 'scale(1.04) translateY(-8px)'
                    : isHovered
                    ? 'scale(1.02) translateY(-4px)'
                    : 'scale(1) translateY(0)',
                  boxShadow: isSelected
                    ? `0 0 0 3px #f97316, 0 0 30px rgba(249,115,22,0.5), 0 0 60px rgba(236,72,153,0.3), 0 25px 50px rgba(0,0,0,0.15)`
                    : isHovered
                    ? `0 0 0 2px rgba(249,115,22,0.4), 0 20px 40px rgba(0,0,0,0.12)`
                    : '0 4px 20px rgba(0,0,0,0.08)',
                }}
              >
                {/* Animated border gradient on selection */}
                {isSelected && (
                  <div
                    className="absolute inset-0 rounded-3xl pointer-events-none z-20"
                    style={{
                      background: 'linear-gradient(135deg, #f97316, #ec4899, #f59e0b, #f97316)',
                      backgroundSize: '300% 300%',
                      animation: 'gradientShift 2s ease infinite',
                      padding: '2px',
                      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                    }}
                  />
                )}

                {/* Product Image */}
                <div className="relative h-72 w-full overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-115"
                    style={{ transform: isSelected || isHovered ? 'scale(1.12)' : 'scale(1)' }}
                  />

                  {/* Gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent transition-opacity duration-300 ${isHovered || isSelected ? 'opacity-100' : 'opacity-0'}`} />

                  {/* Badge */}
                  {product.badge && (
                    <div
                      className={`absolute top-4 right-4 px-3 py-1.5 text-white text-xs font-bold rounded-full transition-all duration-300 ${
                        isSelected ? 'scale-110' : ''
                      }`}
                      style={{
                        background: 'linear-gradient(135deg, #f97316, #ec4899)',
                        boxShadow: isSelected ? '0 0 15px rgba(249,115,22,0.7)' : '0 2px 8px rgba(0,0,0,0.2)',
                      }}
                    >
                      {product.badge}
                    </div>
                  )}

                  {/* Category chip */}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-white text-xs font-medium border border-white/30 capitalize">
                    {product.category}
                  </div>

                  {/* Sparkles on selection */}
                  {isSelected && (
                    <>
                      {[...Array(5)].map((_, si) => (
                        <div
                          key={si}
                          className="absolute w-2 h-2 rounded-full animate-ping"
                          style={{
                            top: `${20 + si * 15}%`,
                            left: `${10 + si * 18}%`,
                            backgroundColor: ['#f97316', '#ec4899', '#f59e0b', '#ef4444', '#fb923c'][si],
                            animationDelay: `${si * 0.15}s`,
                            boxShadow: `0 0 6px ${['#f97316', '#ec4899', '#f59e0b', '#ef4444', '#fb923c'][si]}`,
                          }}
                        />
                      ))}
                    </>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <h3
                    className={`text-lg font-bold mb-1.5 transition-all duration-300 ${
                      isSelected ? 'gradient-text-fire' : 'text-gray-900 group-hover:text-orange-600'
                    }`}
                  >
                    {product.name}
                  </h3>
                  <p className="text-gray-500 text-sm mb-4 line-clamp-2 leading-relaxed">
                    {product.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      <span
                        className="text-2xl font-black"
                        style={{
                          background: 'linear-gradient(135deg, #f97316, #ec4899)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        AED {product.price}
                      </span>
                      {product.original_price && (
                        <span className="text-gray-400 line-through text-sm">AED {product.original_price}</span>
                      )}
                      {product.original_price && (
                        <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                          -{Math.round((1 - product.price / product.original_price) * 100)}%
                        </span>
                      )}
                    </div>

                    <button
                      onClick={(e) => handleAddToCart(e, product)}
                      className={`relative px-5 py-2.5 rounded-full font-semibold text-white text-sm transition-all duration-300 whitespace-nowrap cursor-pointer overflow-hidden ${
                        isAdded ? 'scale-110' : 'hover:scale-110'
                      }`}
                      style={{
                        background: isAdded
                          ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                          : 'linear-gradient(135deg, #f97316, #ec4899)',
                        boxShadow: isAdded
                          ? '0 0 20px rgba(34,197,94,0.5)'
                          : isSelected
                          ? '0 0 20px rgba(249,115,22,0.6)'
                          : 'none',
                      }}
                    >
                      <span className="relative z-10 flex items-center gap-1.5">
                        <i className={`${isAdded ? 'ri-check-line' : 'ri-shopping-cart-line'} transition-all duration-300`}></i>
                        {isAdded ? 'Added!' : 'Add'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Bottom glow bar on selection */}
                {isSelected && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-3xl"
                    style={{ background: 'linear-gradient(90deg, #f97316, #ec4899, #f59e0b)', backgroundSize: '200% 100%', animation: 'gradientShift 2s ease infinite' }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="text-center mt-14">
          <button
            onClick={() => navigate('/shop')}
            className="group relative px-12 py-4 rounded-full font-bold text-white text-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{
              background: `linear-gradient(135deg, ${activeCat.color.includes('orange') ? '#f97316' : '#ec4899'}, #ec4899)`,
              boxShadow: `0 0 30px ${activeCat.glow}, 0 10px 30px rgba(0,0,0,0.15)`,
            }}
          >
            <span className="relative z-10 flex items-center gap-3">
              <i className="ri-store-2-line"></i>
              View All Products
              <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
            </span>
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
        </div>
      </div>
    </section>
  );
}
